const HTML_ESCAPE_CHARACTERS: Readonly<Record<string, string>> = {
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	"'": '&#39;',
};

function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, (character) => HTML_ESCAPE_CHARACTERS[character] ?? character);
}

/**
 * Formats a plain-text social starter description into the HTML paragraph shape the SAP
 * editor (HugeRTE) expects. Callers are the social starter angle flows, ambassador drafts,
 * and the homepage social-post draft — all of which prefill a draft that SAP then opens.
 *
 * Every run of newlines is a paragraph break, so each one becomes its own `<p>` block, and
 * `<br />` is never emitted. Angle descriptions arrive from the petition service separating
 * paragraphs with a *single* `\n`, not a blank line, and this content has no legitimate
 * intra-paragraph line break. Treating those as soft breaks put the whole description in one
 * `<p>`: HugeRTE only applies its `p + p { margin-top: 1.5em }` spacing between sibling
 * paragraphs, so the text rendered with no gaps, and pressing Enter to add one split the
 * block while leaving the `<br />` stranded at the top of the new paragraph — a doubled gap.
 *
 * Deliberately NOT shared with the SAP flow, which keeps its own `toHtmlParagraphs` (see
 * startAPetitionV2/shared/api/generateAiPetitionDraft, and ambassadors/start which uses the
 * same mutation). Those drafts arrive blank-line separated and use single `\n` as a genuine
 * soft break, so their `<br />` handling is correct and must stay.
 */
export function formatAngleDescriptionForSapEditor(description: string): string {
	const normalizedDescription = description.replace(/\r\n/g, '\n').trim();

	if (normalizedDescription.length === 0) return '';

	return normalizedDescription
		.split(/\n+/)
		.map((paragraph) => paragraph.trim())
		.filter((paragraph) => paragraph.length > 0)
		.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
		.join('');
}
